import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Table } from 'react-bootstrap';
import { isAxiosError } from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { AdminApiDomain, getAdminApiUrl } from '../../../config/ApiRouting';
import {
  FulfillmentAdminClient,
  type StaleStockLine,
  type StockTakeDetail,
  type StockTakeRound,
} from '../../../infrastructure/FulfillmentAdminClient';
import { ROUTES } from '../../../routes/routes';
import { kstMinute } from '../../../utils/fulfillmentTime';
import { describeStaleStock, staleStockRefusalOf } from '../../../utils/fulfillmentErrors';
import { asciiScanField } from '@/pages/fulfillment/components/asciiScanField';
import { CameraScanField } from '@/pages/fulfillment/components/CameraScanField';

const GENERIC_ERROR = 'The stock take request failed.';
/** The server refuses a longer count reason, so the field stops the operator before the request does. */
const COUNT_REASON_MAX_LENGTH = 200;
/** A round cannot be walked away from once it has moved stock; undoing that is a different job. */
const CONFIRMED_ELSEWHERE = 'That round has already been confirmed, so it can no longer be cancelled.';
/** Enough rounds for last week's count to be reopened without turning the card into a report. */
const HISTORY_LIMIT = 20;
/** The empty status is every status: the server is asked for the lot rather than the page hiding rows. */
const ALL_STATUSES = '';
interface StatusFilterOption {
  value: string;
  label: string;
}

const STATUS_FILTERS: StatusFilterOption[] = [
  { value: ALL_STATUSES, label: 'All' },
  { value: 'OPEN', label: 'Open' },
  { value: 'CONFIRMED', label: 'Confirmed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

/** The round Open refused to duplicate, as the 409 body names it — not a list row, so it carries no tallies. */
interface StandingRound {
  docId: string;
  locationCode: string | null;
  operator: string | null;
  startedAt: string | null;
}

interface StockTakeErrorBody {
  docId?: string;
  locationCode?: string;
  status?: string;
  startedAt?: string;
  operator?: string;
  message?: string | string[];
}

const stockTakeErrorOf = (err: unknown) => (isAxiosError<StockTakeErrorBody>(err) ? err : undefined);

/**
 * A 409 naming a docId is a round to walk into rather than a failure to report — the same read
 * `startSession` makes of the closing 409. Every other failure stays a failure.
 */
const standingRoundOf = (err: unknown): StandingRound | undefined => {
  const failure = stockTakeErrorOf(err);
  if (failure?.response?.status !== 409) {
    return undefined;
  }
  const body = failure.response.data;
  if (!body?.docId) {
    return undefined;
  }
  return {
    docId: body.docId,
    locationCode: body.locationCode ?? null,
    operator: body.operator ?? null,
    startedAt: body.startedAt ?? null,
  };
};

const statusVariant = (status: string, contested: boolean): string => {
  if (status !== 'OPEN') {
    return status === 'CONFIRMED' ? 'success' : 'secondary';
  }
  return contested ? 'danger' : 'dark';
};

/** A round is found by the KST day and hour it turned, so both stay and the seconds go. */
const formatRoundClock = (iso: string | null | undefined): string => kstMinute(iso);

export default function FulfillmentStockTakePage() {
  const client = useMemo(() => new FulfillmentAdminClient(getAdminApiUrl(AdminApiDomain.FULFILLMENT)), []);
  const [locationCode, setLocationCode] = useState('');
  const [roundIdInput, setRoundIdInput] = useState('');
  const [activeRoundId, setActiveRoundId] = useState('');
  const [sku, setSku] = useState('');
  const [countedQty, setCountedQty] = useState('');
  const [countReason, setCountReason] = useState('');
  const [signoffConfirmed, setSignoffConfirmed] = useState(false);
  const [detail, setDetail] = useState<StockTakeDetail | null>(null);
  const [rounds, setRounds] = useState<StockTakeRound[]>([]);
  const [statusFilter, setStatusFilter] = useState(ALL_STATUSES);
  const [pendingCancel, setPendingCancel] = useState<StockTakeRound | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [standingRound, setStandingRound] = useState<StandingRound | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  /** SKUs whose shelf moved after they were counted; each has to be recounted before the round can be confirmed. */
  const [staleBySku, setStaleBySku] = useState<Record<string, StaleStockLine>>({});
  const roundRequestRef = useRef(0);
  const countQtyRef = useRef<HTMLInputElement>(null);
  /**
   * The key outlives the request that succeeded: a second click on Open then repeats the first call
   * and the server hands back the round it already opened. Clearing it here is what let one shelf
   * carry two rounds, and two rounds counted apart deduct the same shortfall twice.
   */
  const openKeyRef = useRef<{ locationCode: string; idempotencyKey: string } | null>(null);

  const loadRound = useCallback(
    async (docId: string) => {
      const loaded = await client.getStockTake(docId);
      setRoundIdInput(docId);
      setActiveRoundId(docId);
      setDetail(loaded);
    },
    [client],
  );

  // A recount list belongs to the round it was refused on; another round starts clean.
  useEffect(() => {
    setStaleBySku({});
  }, [activeRoundId]);

  /** Marks the SKUs a refusal named, redraws the round as it stands, and says what moved. */
  const markStale = async (docId: string, lines: StaleStockLine[], next: string) => {
    setStaleBySku((standing) => ({ ...standing, ...Object.fromEntries(lines.map((line) => [line.sku, line])) }));
    setError(`${describeStaleStock(lines)} ${next}`);
    const requestId = roundRequestRef.current;
    try {
      const loaded = await client.getStockTake(docId);
      if (requestId === roundRequestRef.current) {
        setDetail(loaded);
      }
    } catch {
      // The refusal already names what moved; a failed re-read leaves the last sheet up.
    }
  };

  const recount = (line: StaleStockLine) => {
    setSku(line.sku);
    setCountedQty('');
    countQtyRef.current?.focus();
  };

  const refreshRounds = useCallback(
    async (status: string) => {
      const listed =
        (await client.listStockTakes({ limit: HISTORY_LIMIT, ...(status ? { status } : {}) })).rounds ?? [];
      setRounds(listed);
      return listed;
    },
    [client],
  );

  useEffect(() => {
    let ignore = false;
    // The day opens on every round; narrowing is the operator's move, not the screen's.
    refreshRounds(ALL_STATUSES)
      .then(async (listed) => {
        // One open round is the day's work and lands under the scanner. Several are a fault the operator
        // has to see whole before counting into any of them, so none is picked for them.
        const stillOpen = listed.filter((round) => round.status === 'OPEN');
        if (!ignore && stillOpen.length === 1) {
          await loadRound(stillOpen[0]!.docId);
        }
      })
      // A build that predates the list endpoint still counts a shelf; it just lists nothing, and says nothing about it.
      .catch(() => undefined);
    return () => {
      ignore = true;
    };
  }, [refreshRounds, loadRound]);

  /** The status is the server's filter, so picking one is a new request, not a pass over the rows already held. */
  const filterByStatus = async (status: string) => {
    setStatusFilter(status);
    setBusy(true);
    setError(null);
    try {
      await refreshRounds(status);
    } catch {
      setError(GENERIC_ERROR);
    } finally {
      setBusy(false);
    }
  };

  /** Opening a past round is a read: the sign-off belongs to the round being left, not the one arriving. */
  const viewRound = async (docId: string) => {
    setBusy(true);
    setError(null);
    setStandingRound(null);
    setConfirmed(false);
    setSignoffConfirmed(false);
    const requestId = ++roundRequestRef.current;
    try {
      const loaded = await client.getStockTake(docId);
      if (requestId === roundRequestRef.current) {
        setRoundIdInput(docId);
        setActiveRoundId(docId);
        setDetail(loaded);
      }
    } catch {
      if (requestId === roundRequestRef.current) {
        setError(GENERIC_ERROR);
      }
    } finally {
      setBusy(false);
    }
  };

  const open = async () => {
    const requestedLocation = locationCode.trim();
    if (!requestedLocation) {
      return;
    }
    setBusy(true);
    setError(null);
    setStandingRound(null);
    setConfirmed(false);
    setActiveRoundId('');
    setDetail(null);
    const requestId = ++roundRequestRef.current;
    if (openKeyRef.current?.locationCode !== requestedLocation) {
      openKeyRef.current = { locationCode: requestedLocation, idempotencyKey: uuidv4() };
    }
    try {
      const opened = await client.openStockTake(openKeyRef.current);
      const loaded = await client.getStockTake(opened.docId);
      if (requestId === roundRequestRef.current) {
        setRoundIdInput(opened.docId);
        setActiveRoundId(opened.docId);
        setDetail(loaded);
      }
      await refreshRounds(statusFilter).catch(() => undefined);
    } catch (err) {
      // The shelf refusing a second round is not a failure to report — it is the round the operator meant to be in.
      const standing = standingRoundOf(err);
      if (requestId === roundRequestRef.current) {
        if (standing) {
          setStandingRound(standing);
        } else {
          setError(GENERIC_ERROR);
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const resume = async () => {
    const requestedRoundId = roundIdInput.trim();
    if (!requestedRoundId) {
      return;
    }
    setBusy(true);
    setError(null);
    setConfirmed(false);
    setActiveRoundId('');
    setDetail(null);
    const requestId = ++roundRequestRef.current;
    try {
      const loaded = await client.getStockTake(requestedRoundId);
      if (requestId === roundRequestRef.current) {
        setActiveRoundId(requestedRoundId);
        setDetail(loaded);
      }
    } catch {
      if (requestId === roundRequestRef.current) {
        setError(GENERIC_ERROR);
      }
    } finally {
      setBusy(false);
    }
  };

  const count = async () => {
    const quantity = Number(countedQty);
    if (
      !activeRoundId ||
      detail?.doc?.['status'] !== 'OPEN' ||
      !sku.trim() ||
      !Number.isInteger(quantity) ||
      quantity < 0
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    const countedSku = sku.trim();
    // A recount is made against the balance the refusal reported, so a shelf that moved again is caught too.
    const stale = staleBySku[countedSku];
    try {
      const reason = countReason.trim();
      await client.countStockTake(activeRoundId, {
        sku: countedSku,
        countedQty: quantity,
        ...(reason ? { reason } : {}),
        ...(stale ? { expectedSystemQty: stale.current } : {}),
      });
      // The recount mark comes off only once the sheet shows the recounted line; a failed re-read keeps it.
      setDetail(await client.getStockTake(activeRoundId));
      setStaleBySku((standing) => {
        const rest = { ...standing };
        delete rest[countedSku];
        return rest;
      });
      setSku('');
      setCountedQty('');
      setCountReason('');
    } catch (err) {
      // The typed count stays in the form: nothing was written, and the operator recounts against the new balance.
      const refusal = staleStockRefusalOf(err);
      if (refusal) {
        await markStale(activeRoundId, refusal.staleLines, 'Recount it, then record the count again.');
      } else {
        setError(GENERIC_ERROR);
      }
    } finally {
      setBusy(false);
    }
  };

  const staleLines = Object.values(staleBySku);

  const confirm = async () => {
    if (!activeRoundId || detail?.doc?.['status'] !== 'OPEN' || !signoffConfirmed || staleLines.length > 0) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await client.confirmStockTake(activeRoundId);
      // The round this key opened is finished, so the next count of that shelf is allowed to be a new one.
      openKeyRef.current = null;
      setDetail(await client.getStockTake(activeRoundId));
      setConfirmed(true);
      setSignoffConfirmed(false);
      await refreshRounds(statusFilter).catch(() => undefined);
    } catch (err) {
      // Nothing was adjusted and the round is still open; the moved SKUs are recounted before the next sign-off.
      const refusal = staleStockRefusalOf(err);
      if (refusal) {
        setSignoffConfirmed(false);
        await markStale(activeRoundId, refusal.staleLines, 'Recount these items, then confirm.');
      } else {
        setError(GENERIC_ERROR);
      }
    } finally {
      setBusy(false);
    }
  };

  const closeCancelPrompt = () => {
    setPendingCancel(null);
    setCancelReason('');
  };

  const cancelRound = async (round: StockTakeRound) => {
    const reason = cancelReason.trim();
    closeCancelPrompt();
    setBusy(true);
    setError(null);
    try {
      await client.cancelStockTake(round.docId, reason || undefined);
      // The round that key opened is gone, so counting this shelf again has to start a new one.
      openKeyRef.current = null;
      if (round.docId === activeRoundId) {
        roundRequestRef.current += 1;
        setActiveRoundId('');
        setDetail(null);
        setConfirmed(false);
        setSignoffConfirmed(false);
      }
      await refreshRounds(statusFilter).catch(() => undefined);
    } catch (err) {
      // A 409 here means the row was stale: someone signed the round off between the list and the click.
      const confirmedElsewhere = stockTakeErrorOf(err)?.response?.status === 409;
      setError(confirmedElsewhere ? CONFIRMED_ELSEWHERE : GENERIC_ERROR);
      if (confirmedElsewhere) {
        await refreshRounds(statusFilter).catch(() => undefined);
      }
    } finally {
      setBusy(false);
    }
  };

  const openRounds = rounds.filter((round) => round.status === 'OPEN');
  // More than one round standing open is not a choice on offer — it is the state that double-deducts.
  const contested = openRounds.length > 1;

  return (
    <Container fluid className="py-3">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h2 className="mb-1">Stock Take</h2>
          <div className="text-muted small">
            Open a count round, record the counted quantity per SKU, then commit the differences.
          </div>
        </div>
        <Link to={ROUTES.fulfillment.inventory} className="btn btn-outline-secondary btn-sm">
          Back to inventory
        </Link>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {confirmed && <Alert variant="success">The stock take was committed.</Alert>}

      {standingRound && (
        <Alert variant="warning">
          <div className="fw-semibold">{standingRound.locationCode ?? 'That shelf'} already carries an open round</div>
          <div>
            Opened {formatRoundClock(standingRound.startedAt)} by {standingRound.operator ?? 'an unnamed operator'}. A
            second round over the same shelf counts against the same stock twice, so this one was not opened — finish
            the standing one instead.
          </div>
          <Button
            variant="warning"
            size="sm"
            className="mt-2"
            disabled={busy}
            onClick={() => void viewRound(standingRound.docId)}
          >
            Open the standing round
          </Button>
        </Alert>
      )}

      {contested && (
        <Alert variant="danger">
          <div className="fw-semibold">{openRounds.length} rounds are open at once</div>
          <div>
            A shelf may carry only one open round, and opening a second on the same shelf is refused. Two rounds over
            the same shelf each snapshot the system quantity as it stood when they opened, so confirming both deducts
            the same shortfall twice — a shelf of 10 counted as 8 in each ends at 6.
          </div>
          <div>
            None has been opened for you. Settle the rounds that should not exist before counting into any of them.
          </div>
        </Alert>
      )}

      <Card className="mb-3">
        <Card.Body>
          <Row className="g-3">
            <Col md={3}>
              <Form.Group controlId="take-location">
                <Form.Label>Location code</Form.Label>
                <CameraScanField label="Scan location code with camera" disabled={busy}>
                  <Form.Control value={locationCode} disabled={busy} {...asciiScanField(setLocationCode)} />
                </CameraScanField>
              </Form.Group>
            </Col>
            <Col md="auto" className="d-flex align-items-end">
              <Button disabled={busy || !locationCode.trim()} onClick={() => void open()}>
                Open round
              </Button>
            </Col>
            <Col md={4}>
              <Form.Group controlId="take-doc">
                <Form.Label>Round ID</Form.Label>
                <Form.Control
                  value={roundIdInput}
                  onChange={(event) => {
                    roundRequestRef.current += 1;
                    setRoundIdInput(event.target.value);
                    setActiveRoundId('');
                    setDetail(null);
                    setConfirmed(false);
                    setSignoffConfirmed(false);
                  }}
                />
              </Form.Group>
            </Col>
            <Col md="auto" className="d-flex align-items-end">
              <Button variant="outline-secondary" disabled={busy || !roundIdInput.trim()} onClick={() => void resume()}>
                Resume
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {activeRoundId && detail && (
        <>
          <Card className="mb-3">
            <Card.Header className="fw-semibold">
              Count <Badge bg="secondary">{activeRoundId}</Badge>
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col md={4}>
                  <Form.Group controlId="take-sku">
                    <Form.Label>Count SKU</Form.Label>
                    <CameraScanField label="Scan SKU with camera">
                      <Form.Control value={sku} onChange={(event) => setSku(event.target.value)} />
                    </CameraScanField>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group controlId="take-count">
                    <Form.Label>Counted quantity</Form.Label>
                    <Form.Control
                      ref={countQtyRef}
                      type="number"
                      min={0}
                      step={1}
                      value={countedQty}
                      onChange={(event) => setCountedQty(event.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group controlId="take-reason">
                    <Form.Label>Count reason (optional)</Form.Label>
                    <Form.Control
                      value={countReason}
                      maxLength={COUNT_REASON_MAX_LENGTH}
                      onChange={(event) => setCountReason(event.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md="auto" className="d-flex align-items-end">
                  <Button
                    disabled={
                      busy ||
                      detail.doc?.['status'] !== 'OPEN' ||
                      !sku.trim() ||
                      countedQty === '' ||
                      !Number.isInteger(Number(countedQty)) ||
                      Number(countedQty) < 0
                    }
                    onClick={() => void count()}
                  >
                    Record count
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span className="fw-semibold">Diff sheet ({detail?.tallies.discrepancies ?? 0} discrepancies)</span>
              <div className="d-flex gap-2 align-items-end">
                <Form.Check
                  id="take-signoff-confirm"
                  label="I confirm adjustments"
                  checked={signoffConfirmed}
                  onChange={(event) => setSignoffConfirmed(event.target.checked)}
                />
                <Button
                  variant="danger"
                  size="sm"
                  disabled={busy || detail.doc?.['status'] !== 'OPEN' || !signoffConfirmed || staleLines.length > 0}
                  title={staleLines.length > 0 ? 'Recount the highlighted SKUs first' : undefined}
                  onClick={() => void confirm()}
                >
                  Confirm adjustments
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0 align-middle">
                <thead className="table-dark">
                  <tr>
                    <th>SKU</th>
                    <th className="text-end">System</th>
                    <th className="text-end">Counted</th>
                    <th className="text-end">Diff</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {(detail?.lines ?? []).map((line) => {
                    const stale = staleBySku[line.sku];
                    return (
                      <tr key={line.lineNo} className={stale ? 'table-warning' : undefined}>
                        <td className="font-monospace">
                          {line.sku}
                          {stale && (
                            <div className="small text-danger">
                              Stock changed: counted against {stale.countedAgainst}, now {stale.current}.{' '}
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0 align-baseline"
                                disabled={busy}
                                onClick={() => recount(stale)}
                              >
                                Recount
                              </Button>
                            </div>
                          )}
                        </td>
                        <td className="text-end">{line.systemQty}</td>
                        <td className="text-end">{line.countedQty}</td>
                        <td className={`text-end ${line.diff !== 0 ? 'text-danger fw-semibold' : ''}`}>{line.diff}</td>
                        <td>{line.reason ?? '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </>
      )}

      <Card className="mt-3">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span className="fw-semibold">Count rounds</span>
          <Form.Group controlId="take-status-filter" className="d-flex align-items-center gap-2 mb-0">
            <Form.Label className="mb-0 small text-muted">Status</Form.Label>
            <Form.Select
              size="sm"
              value={statusFilter}
              disabled={busy}
              onChange={(event) => void filterByStatus(event.target.value)}
            >
              {STATUS_FILTERS.map((filter) => (
                <option key={filter.label} value={filter.value}>
                  {filter.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Card.Header>
        <Card.Body className="p-0">
          {rounds.length === 0 ? (
            <div className="p-3 text-muted small">No count rounds to show.</div>
          ) : (
            <Table responsive hover className="mb-0 align-middle" aria-label="Count rounds">
              <thead className="table-dark">
                <tr>
                  <th>Round</th>
                  <th>Location</th>
                  <th>Opened</th>
                  <th>Status</th>
                  <th>Operator</th>
                  <th>Confirmed</th>
                  <th className="text-end">Adjusted</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rounds.map((round) => (
                  <tr key={round.docId} className={contested && round.status === 'OPEN' ? 'table-danger' : undefined}>
                    <td className="font-monospace">{round.docId}</td>
                    <td className="font-monospace">{round.locationCode}</td>
                    <td className="text-nowrap">{formatRoundClock(round.startedAt)}</td>
                    <td>
                      <Badge bg={statusVariant(round.status, contested)}>{round.status}</Badge>
                    </td>
                    <td>{round.operator ?? '—'}</td>
                    <td className="text-nowrap">
                      {formatRoundClock(round.confirmedAt)}
                      {round.signedOffBy && <div className="text-muted small">{round.signedOffBy}</div>}
                    </td>
                    {/* A cancelled round reads zero here forever, which is the whole of what it did to the stock. */}
                    <td className="text-end">{round.adjusted}</td>
                    <td className="text-end">
                      <div className="d-flex gap-2 justify-content-end">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          disabled={busy || round.docId === activeRoundId}
                          onClick={() => void viewRound(round.docId)}
                        >
                          Open
                        </Button>
                        {/* Only a round still open can be walked away from; a confirmed one has already moved stock. */}
                        {round.status === 'OPEN' && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            disabled={busy}
                            onClick={() => setPendingCancel(round)}
                          >
                            Cancel
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={pendingCancel != null} onHide={closeCancelPrompt} centered>
        <Modal.Body>
          <div className="fw-semibold">Cancel this count round?</div>
          {pendingCancel && (
            <div className="text-muted small mt-1 font-monospace">
              {pendingCancel.docId} · {pendingCancel.locationCode ?? '—'}
            </div>
          )}
          <div className="mt-2">
            The round closes and nothing it counted is applied — no stock moves, no adjustment. Counting this shelf
            means opening a new round.
          </div>
          <Form.Group controlId="take-cancel-reason" className="mt-3">
            <Form.Label className="small text-muted mb-1">Reason (optional)</Form.Label>
            <Form.Control size="sm" value={cancelReason} onChange={(event) => setCancelReason(event.target.value)} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={closeCancelPrompt}>
            No — leave it open
          </Button>
          <Button variant="danger" size="sm" onClick={() => pendingCancel && void cancelRound(pendingCancel)}>
            Yes — cancel the round
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
